import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import FloatToCurrency from 'app/helpers/FloatToCurrency';
import { resolveCurrentTier } from 'app/hooks/useSubscriptionPlans/resolveCurrentTier';

export const formatPlanDate = (dateString) => {
  if (!dateString) {
    return '-';
  }

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const EMPTY_SUBSCRIPTION_STATE = {
  status: null,
  hasError: false,
  errorCode: null,
  subscriptionId: null,
  expirationDate: null,
  startDate: null,
  planAmount: null,
  chargedAmount: null,
  nextChargeAmount: null,
  nextChargeDateRaw: null,
  plan: null,
  provider: null,
  paymentMethod: null,
  cardBrand: null,
  cardLastDigits: null,
  coupon: null,
  periodicity: null,
  addons: [],
  coupons: [],
  // v2/subscriptions doesn't provide has_jusprocessos/jus_processos_qtd (feature
  // entitlement, not gateway/billing data) -- these are now always the empty
  // default here. Consumers checking JusProcessos access should read
  // useCredits().hasJusProcessos (credits-driven) instead.
  hasJusProcessos: false,
  jusProcessosQtd: 0,
  tickets: [],
  isEmpty: true,

  isExpired: false,

  isCanceled: false,
  isTrialExpired: false,
  isUnpaid: false,
  isWaitingPayment: false,
  isPendingPayment: false,
  isDelayed: false,
  isPendingReactivation: false,
  isFailed: false,
  isPaid: false,
  isTrialing: false,
  isOk: false,

  isLoading: false,
  isActive: false,
  isInactive: false,
  currentTier: 'grandfathered',

  planName: '-',
  amountValue: '-',
  nextChargeDate: '-',
};

const hasDisplayablePlanData = subscription =>
  Boolean(
    subscription?.info?.plan ||
      subscription?.info?.subscription_id ||
      subscription?.plan_details,
  );

const parseSubscriptionData = subscription => ({
  subscriptionId: subscription?.info?.subscription_id ?? null,
  plan:
    subscription?.plan_details?.name ||
    subscription?.info?.plan ||
    subscription?.plan_details?.plan_type ||
    null,
  planAmount:
    subscription?.info?.plan_amount ?? subscription?.plan_details?.amount ?? null,
  chargedAmount: subscription?.info?.charged_amount ?? null,
  nextChargeAmount: subscription?.info?.next_charge_amount ?? null,
  nextChargeDateRaw: subscription?.info?.next_charge_date ?? null,
  startDate: subscription?.info?.current_period_start,
  expirationDate: subscription?.info?.current_period_end,
  status: subscription?.info?.status || subscription?.subscription_info,
  cardBrand: subscription?.info?.card_brand || null,
  cardLastDigits: subscription?.info?.card_last_digits || null,
  paymentMethod: subscription?.info?.payment_method || null,
  periodicity: subscription?.plan_details?.periodicity ?? null,
  addons: subscription?.info?.addons ?? [],
  coupons: subscription?.info?.coupons ?? [],
  hasError: subscription?.info?.has_error || false,
  coupon: subscription?.info?.coupon || null,
  provider: subscription?.info?.provider || null,
  errorCode: subscription?.error_code || null,
  hasJusProcessos: subscription?.has_jusprocessos || false,
  jusProcessosQtd: subscription?.jus_processos_qtd || 0,
  tickets: subscription?.info?.tickets || [],
  isEmpty: false,

  isExpired: subscription?.isExpired || false,

  isCanceled: subscription?.isCanceled || false,
  isTrialExpired: subscription?.isTrialExpired || false,
  isUnpaid: subscription?.isUnpaid || false,
  isWaitingPayment: subscription?.isWaitingPayment || false,
  isPendingPayment: subscription?.isPendingPayment || false,
  isDelayed:
    subscription?.isDelayed ||
    String(
      subscription?.info?.status || subscription?.subscription_info || '',
    ).toLowerCase() === 'delayed',
  isPendingReactivation: subscription?.isPendingReactivation || false,
  isFailed: subscription?.isFailed || false,
  isPaid: subscription?.isPaid || false,
  isTrialing: subscription?.isTrialing || false,
  isOk: subscription?.isOk || false,
  isActive: subscription?.isActive || false,

  isLoading: false,

  isInactive: subscription?.isInactive || false,
  currentTier: resolveCurrentTier(subscription?.plan_details?.gateway_id),
});

const useSubscriptionStatus = () => {
  const { subscription, isLoading } = useSelector((state) => ({
    subscription: state.subscription.subscription_status,
    isLoading: state.subscription.is_subscription_status_loading,
  }));

  const subscriptionStatus = useMemo(() => {
    if (isLoading) {
      return {
        ...EMPTY_SUBSCRIPTION_STATE,
        isLoading: true,
        isEmpty: false,
      };
    }

    if (!subscription || Object.keys(subscription).length === 0) {
      return {
        ...EMPTY_SUBSCRIPTION_STATE,
        isLoading: false,
      };
    }

    if (subscription.info?.has_error && !hasDisplayablePlanData(subscription)) {
      return {
        ...EMPTY_SUBSCRIPTION_STATE,
        isLoading: false,
      };
    }

    const parsedData = parseSubscriptionData(subscription);

    return {
      ...parsedData,
      hasError: subscription?.info?.has_error || false,
      isExpired: subscription?.isExpired ?? parsedData.isExpired,
      isCanceled: subscription?.isCanceled ?? parsedData.isCanceled,
      isTrialExpired: subscription?.isTrialExpired ?? parsedData.isTrialExpired,
      isUnpaid: subscription?.isUnpaid ?? parsedData.isUnpaid,
      isWaitingPayment:
        subscription?.isWaitingPayment ?? parsedData.isWaitingPayment,
      isPendingPayment:
        subscription?.isPendingPayment ?? parsedData.isPendingPayment,
      isFailed: subscription?.isFailed ?? parsedData.isFailed,
      isPaid: subscription?.isPaid ?? parsedData.isPaid,
      isTrialing: subscription?.isTrialing ?? parsedData.isTrialing,
      isOk: subscription?.isOk ?? parsedData.isOk,
      isActive: subscription?.isActive ?? parsedData.isActive,
      isInactive: subscription?.isInactive ?? parsedData.isInactive,
      isLoading: false,
    };
  }, [subscription, isLoading]);

  return useMemo(() => {
    const paymentMethodNormalized = String(
      subscriptionStatus.paymentMethod ?? "",
    ).toLowerCase();
    const isBoletoSubscription = paymentMethodNormalized === "boleto";
    const isPixSubscription = paymentMethodNormalized === "pix";
    const isCreditCardSubscription = paymentMethodNormalized === "credit_card";
    const planName = subscriptionStatus.plan ?? '-';
    const effectiveChargeAmount =
      subscriptionStatus.nextChargeAmount ??
      subscriptionStatus.chargedAmount ??
      subscriptionStatus.planAmount;
    const amountValue =
      effectiveChargeAmount != null
        ? `R$ ${FloatToCurrency(effectiveChargeAmount)}`
        : '-';
    const displayedPlanDate = subscriptionStatus.isCanceled
      ? subscriptionStatus.expirationDate
      : subscriptionStatus.nextChargeDateRaw ?? subscriptionStatus.expirationDate;
    const nextChargeDate = formatPlanDate(displayedPlanDate);

    return {
      ...subscriptionStatus,
      planName,
      amountValue,
      nextChargeDate,
      paymentMethodNormalized,
      isBoletoSubscription,
      isPixSubscription,
      isCreditCardSubscription,
      isPixOrBoletoSubscription:
        isBoletoSubscription || isPixSubscription,
    };
  }, [subscriptionStatus]);
};

export default useSubscriptionStatus;
